'use client';

import { useState, useCallback } from 'react';
import { tmaActions } from '../bootstrap/tmaActions';
import { useApiClient } from '../contexts/ApiContext';
import { useRuntime } from '../runtime/RuntimeContext';
import { getBotUsername, getMiniAppName } from '../utils/config';
import { bustCache } from '../components/RushMid/render/battleAssetVersion';
import { shareOrCopyLink } from '../lib/browserSurface';
import { isShareSheetDismissed, isTelegramShareCancelled } from '../lib/shareErrors';

const GIF_PATH = bustCache('/battle/loading.mp4');

export function useGameShare(matchId: string, messageText: string, buttonText: string) {
  const [isSharing, setIsSharing] = useState(false);
  const apiClient = useApiClient();
  const runtime = useRuntime();

  const share = useCallback(async () => {
    if (!matchId || isSharing) return;

    const link =
      runtime === 'web'
        ? `${window.location.origin}/games/rush-mid?game=${matchId}`
        : `https://t.me/${getBotUsername()}/${getMiniAppName()}?startapp=game_${matchId}`;

    if (await tmaActions.isShareMessageAvailable()) {
      setIsSharing(true);
      try {
        const { preparedMessageId } = await apiClient.api.userControllerSavePreparedInlineMessage({
          gifUrl: `${window.location.origin}${GIF_PATH}`,
          caption: messageText,
          title: 'SHOT',
          button: { text: buttonText, url: link },
        });
        await tmaActions.shareMessage(preparedMessageId);
      } catch (error: unknown) {
        if (isTelegramShareCancelled(error)) return;
        throw error;
      } finally {
        setIsSharing(false);
      }
    } else if (window.location.hash.includes('tgWebAppData')) {
      await tmaActions.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(messageText)}`,
      );
    } else {
      try {
        await shareOrCopyLink(messageText, link);
      } catch (error: unknown) {
        if (isShareSheetDismissed(error)) return;
        throw error;
      }
    }
  }, [matchId, isSharing, apiClient, runtime, messageText, buttonText]);

  return { share, isSharing };
}
